import { useTheme } from "@mui/material";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import dayjs from "dayjs";

import { Box } from "@/Components/v2/atoms/Box";
import { OutlinedButton } from "@/Components/v2/atoms/Button";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";

import { ScenarioType, ScenarioTypeEnum } from "../../scenario-form/types";
import {
  CreateScenarioResponse,
  useCreateScenario,
} from "@/pages/scenario-builder/v3/api/createScenario";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useAuth0 } from "@auth0/auth0-react";
import { useUpdateScenarioV3 } from "@/pages/scenario-builder/v3/api/updateScenario";
import { useFeatureGate } from "@statsig/react-bindings";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useScenariosV3Routes } from "@/pages/scenario-builder/v3/hooks/useScenariosV3Routes";
import { useMemo } from "react";
import { useDateRanges } from "@/pages/scenario-builder/v3/api/getDateRanges";
import { ExtendedActionPlanDetail } from "../../../api/v2/getActionPlan";

type ScenarioBannerProps = {
  actionPlan: ExtendedActionPlanDetail;
};

export const ScenarioBanner = ({ actionPlan }: ScenarioBannerProps) => {
  const theme = useTheme();
  const { user } = useAuth0();
  const { navigateToScenarioForm } = useScenariosV3Routes();
  const { scenarioCreated } = useAnalytics();
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  const { scopeBrand, scopeGeographies, spendPlannedTotal, spendPlannedToDate, endDate } =
    actionPlan;

  const handleCreateNewScenarioSuccess = (result: CreateScenarioResponse) => {
    if (result.uuid) {
      scenarioCreated({
        scenarioType: ScenarioType.toString(result.scenarioType as ScenarioTypeEnum),
        scenarioId: result.uuid,
        scenarioName: result.name,
        isCloned: false,
        isQuickScenario: false,
      });
    }
  };

  const handleCreateNewScenarioError = (error: AxiosError) => {
    toast.error("Failed to create new scenario. Please try again.");
    console.error("Create new scenario error:", error);
  };

  const createNewScenario = useCreateScenario({
    config: {
      onSuccess: handleCreateNewScenarioSuccess,
      onError: handleCreateNewScenarioError,
    },
    isScenariosApiEnabled,
  });

  const updateScenario = useUpdateScenarioV3({ isScenariosApiEnabled });

  const { data: dateRanges } = useDateRanges({
    geography: scopeGeographies?.[0],
    config: {
      refetchOnWindowFocus: false,
    },
    isScenariosApiEnabled,
  });

  const scenarioStartDate = useMemo(() => {
    if (!dateRanges?.startDate) return null;
    return dayjs.utc(dateRanges.startDate);
  }, [dateRanges]);

  const handleCreateNewScenario = async () => {
    try {
      if (actionPlan) {
        toast.info("Creating new scenario");
        const newScenario = await createNewScenario.mutateAsync({
          scenarioType: "budget",
          createdBy: user?.email ?? "",
          name: "Untitled Scenario",
        });
        if (!newScenario.uuid) {
          throw new Error("Failed to create scenario - no UUID returned");
        }

        const totalBudget = Math.floor(spendPlannedTotal - spendPlannedToDate);

        await updateScenario.mutateAsync({
          scenarioUUID: newScenario.uuid,
          scenario: {
            brand: scopeBrand,
            geography: scopeGeographies?.[0],
            startDate: scenarioStartDate?.toISOString() ?? null,
            endDate: endDate.toISOString(),
            value: totalBudget,
          },
        });
        toast.success("Scenario created successfully!");

        navigateToScenarioForm(newScenario.uuid);
      }
    } catch (error) {
      console.error("Create new scenario error:", error);
      toast.error("Failed to create new scenario");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.infoBanner.lightBackground,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Stack>
          <Typography variant="body2" fontWeight={600}>
            Want to create your own scenario?
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth="800px">
            Duplicate a recommended scenario and edit the spend constraints, or create a new
            scenario using the same business unit, end date and geography of this Action Plan.
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={2}>
        <OutlinedButton
          variant="outlined"
          onClick={handleCreateNewScenario}
          disabled={createNewScenario.isPending || updateScenario.isPending}
          sx={{
            textAlign: "center",
            width: "185px",
            whiteSpace: "nowrap",
          }}
        >
          {createNewScenario.isPending || updateScenario.isPending
            ? "Creating..."
            : "Create New Scenario"}
        </OutlinedButton>
      </Stack>
    </Box>
  );
};
